import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOMeta from "@/components/base/SEOMeta";

const milestones = [
  { year: "2011", title: "品牌创立", description: "安大师在北京正式成立，推出首套家庭安防解决方案" },
  { year: "2013", title: "企业服务上线", description: "拓展企业防护业务，为超过500家企业提供安防服务" },
  { year: "2015", title: "技术突破", description: "自主研发AI智能分析系统，行业首创主动预警功能" },
  { year: "2017", title: "全国布局", description: "服务网络覆盖全国30+城市，团队规模突破200人" },
  { year: "2020", title: "数字化升级", description: "推出物联网安防平台，实现全设备智能联动与远程管理" },
  { year: "2023", title: "行业领先", description: "累计服务客户突破50,000家，客户满意度达98.6%" },
  { year: "2025", title: "智慧安全生态", description: "构建覆盖家庭、企业、个人的全方位智慧安全生态体系" },
];

const honors = [
  { title: "中国安防行业十大品牌", year: "2025" },
  { title: "国家高新技术企业认证", year: "2024" },
  { title: "ISO 27001 信息安全管理认证", year: "2024" },
  { title: "全国质量诚信标杆企业", year: "2023" },
  { title: "智能安防创新产品奖", year: "2023" },
  { title: "消费者最信赖品牌", year: "2022" },
];

export default function About() {
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "安大师",
    url: "https://www.anmaster.com/",
    description: "安大师是您值得信赖的专业安全防护品牌，提供全面的安全解决方案与专家级服务。",
    foundingDate: "2011",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+86-400-888-9999",
      contactType: "customer service",
      availableLanguage: ["Chinese"],
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "中关村科技园区南路18号",
      addressLocality: "北京市",
      addressRegion: "北京市",
      postalCode: "100080",
      addressCountry: "CN",
    },
  };

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于安大师 - 专业安全防护品牌",
    description: "了解安大师的品牌故事、发展历程、品牌荣誉与联系方式。15年专注安全防护，服务超过50,000家客户。",
    url: "https://www.anmaster.com/about",
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于安大师 - 专业安全防护品牌"
        description="了解安大师的品牌故事、发展历程、品牌荣誉与联系方式。15年专注安全防护，服务超过50,000家客户。"
        keywords="安大师,安大师品牌介绍,安大师联系方式,安大师评价,安全防护品牌"
        canonicalUrl="https://www.anmaster.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <div className="relative h-52 md:h-64 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20professional%20modern%20minimalist%20office%20building%20interior%20with%20clean%20design%2C%20warm%20natural%20lighting%2C%20elegant%20corporate%20headquarters%20atmosphere%2C%20spacious%20lobby%2C%20security%20industry%20aesthetic%2C%20architectural%20photography%2C%20high%20quality&width=1200&height=450&seq=about-header&orientation=landscape"
            alt="安大师品牌"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
              <h1 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mt-4">
                关于安大师
              </h1>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-6">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    安大师成立于<strong>2011年</strong>，总部位于北京市中关村科技园区。作为国内领先的专业安全防护品牌，安大师始终秉持<strong>&ldquo;让安全无忧成为日常&rdquo;</strong>的品牌使命，致力于为家庭、企业和个人提供全方位的安全解决方案。
                  </p>
                  <p>
                    经过<strong>15年</strong>的深耕发展，安大师已建立起覆盖智能硬件、软件平台、专业服务的完整安全防护生态体系。我们的专业团队由超过<strong>200名</strong>安全领域专家组成，涵盖安防工程、网络安全、消防工程、风险管理等多个专业方向。
                  </p>
                  <p>
                    截至目前，安大师已累计服务超过<strong>50,000家</strong>客户，业务覆盖全国30余座主要城市，客户满意度持续保持在<strong>98.6%</strong>以上。无论是高端住宅小区、大型企业集团，还是独居人士，安大师都能提供量身定制的安全防护方案。
                  </p>
                  <p>
                    安大师高度重视技术创新，每年投入营收的<strong>12%</strong>用于研发。自主研发的AI智能行为分析系统、物联网安全平台等技术在行业内处于领先地位。同时，安大师拥有多项国家专利和软件著作权，并已通过ISO 27001信息安全管理体系认证。
                  </p>
                </div>
              </div>
              <div className="relative h-80 md:h-96 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20professional%20security%20team%20in%20modern%20office%20setting%2C%20diverse%20group%20of%20experts%20collaborating%2C%20clean%20corporate%20environment%2C%20warm%20natural%20lighting%2C%20professional%20headshots%20style%2C%20confidence%20and%20trustworthiness%2C%20editorial%20photography%2C%20high%20quality&width=700&height=600&seq=about-team&orientation=portrait"
                  alt="安大师团队"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-12 md:mb-16">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`flex flex-col md:flex-row items-start gap-6 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    <div className="flex-1 flex items-start gap-4 md:justify-end md:pr-8">
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center md:absolute md:left-1/2 md:-translate-x-1/2 z-10`}
                        style={{ marginTop: "2px" }}
                      >
                        <i className="ri-check-line text-sm text-background-50"></i>
                      </div>
                      <div
                        className={`flex-1 bg-background-50 rounded-lg p-5 border border-background-200/50 ${
                          index % 2 === 0 ? "md:text-right" : "md:text-left"
                        }`}
                      >
                        <span className="text-xs font-bold text-primary-600 uppercase tracking-wide">
                          {item.year}
                        </span>
                        <h3 className="text-base font-semibold text-foreground-900 font-heading mt-1 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="hidden md:block flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Honors */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-12 md:mb-16">
              品牌荣誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {honors.map((honor, index) => (
                <div
                  key={honor.title}
                  className="bg-background-100 rounded-lg p-6 border border-background-200/50 text-center transition-all duration-300 hover:border-accent-300/50 group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-accent-200 transition-colors duration-300">
                    <i className="ri-award-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-2">
                    {honor.title}
                  </h3>
                  <span className="text-xs text-accent-600 font-medium">{honor.year}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-24 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-12 md:mb-16">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 max-w-5xl mx-auto">
              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">服务热线</h3>
                    <p className="text-sm text-foreground-600">400-888-9999</p>
                    <p className="text-xs text-foreground-500 mt-0.5">7×24小时全天候服务</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">service@anmaster.com</p>
                    <p className="text-xs text-foreground-500 mt-0.5">工作日24小时内回复</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-600">北京市海淀区中关村科技园区南路18号</p>
                    <p className="text-xs text-foreground-500 mt-0.5">安大师总部大厦</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                    <p className="text-xs text-foreground-500 mt-0.5">周末及节假日应急热线畅通</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                  >
                    立即预约免费评估
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-8 border border-background-200/50">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3055.998765510043!2d116.31874341534995!3d39.98596097941724!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f0540f2f7e0e51%3A0x6c5d2a2f2bd65833!2sZhongguancun%2C%20Haidian%2C%20Beijing%2C%20China!5e0!3m2!1sen!2sus!4v1691400000000"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="安大师总部位置"
                  className="rounded-md"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}